import type { Application } from "pixi.js";
import { useEffect, useRef } from "react";
import { attachCamera, type Camera, type CameraOptions } from "@/lib/pixi/camera";
import { createApp, createStageLayers, type StageLayers } from "@/lib/pixi/createApp";

export interface PixiStageHandle {
  app: Application;
  layers: StageLayers;
  camera: Camera;
}

export type OnReadyCleanup = () => void;
export interface PixiStageProps {
  onReady: (handle: PixiStageHandle) => OnReadyCleanup | undefined;
  initialZoom?: number;
  /** カメラのパン操作を有効化するボタン (default: 全て有効) */
  panButtons?: CameraOptions["panButtons"];
}

/**
 * Pixi.js の Application を React ライフサイクルに閉じ込めるコンポーネント。
 * onReady で handle を返して、呼び出し側で Container に描画を足していく。
 */
export function PixiStage({ onReady, initialZoom = 3, panButtons }: PixiStageProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let disposed = false;
    let cleanupUser: OnReadyCleanup | undefined;
    let handle: PixiStageHandle | null = null;

    const rect = host.getBoundingClientRect();
    const width = Math.max(1, rect.width);
    const height = Math.max(1, rect.height);

    createApp({ width, height })
      .then((app) => {
        if (disposed) {
          app.destroy(true, { children: true, texture: true });
          return;
        }
        host.appendChild(app.canvas);
        app.canvas.style.display = "block";
        app.canvas.style.width = "100%";
        app.canvas.style.height = "100%";

        const layers = createStageLayers(app);
        // ワールドを画面中央に寄せる初期オフセット
        const camera = attachCamera(app, layers.world, {
          initialZoom,
          centerOffset: { x: width / 2, y: height / 3 },
          ...(panButtons ? { panButtons } : {}),
        });
        camera.setPan(width / 2, height / 3);

        handle = { app, layers, camera };
        cleanupUser = onReady(handle);

        // リサイズ対応
        const ro = new ResizeObserver((entries) => {
          if (!entries[0]) return;
          const { width: w, height: h } = entries[0].contentRect;
          app.renderer.resize(Math.max(1, w), Math.max(1, h));
        });
        ro.observe(host);

        // teardown 用に付けておく
        (handle as PixiStageHandle & { _ro?: ResizeObserver })._ro = ro;
      })
      .catch((err: unknown) => {
        console.error("[PixiStage] failed to create application", err);
      });

    return () => {
      disposed = true;
      if (cleanupUser) cleanupUser();
      if (handle) {
        const h = handle as PixiStageHandle & { _ro?: ResizeObserver };
        h._ro?.disconnect();
        handle.camera.dispose();
        try {
          handle.app.destroy(true, { children: true, texture: true });
        } catch (err) {
          console.warn("[PixiStage] destroy error", err);
        }
      }
    };
  }, [onReady, initialZoom, panButtons]);

  return (
    <div
      ref={hostRef}
      style={{
        width: "100%",
        height: "100%",
        position: "absolute",
        inset: 0,
        touchAction: "none",
      }}
    />
  );
}
